/**
 * Which recipe document a command writes.
 *
 * This is the filter that turns one flat command log into a **per-recipe
 * timeline** (PLAN-LAUNCH D3): the Durable Object asks this question of every
 * command it applies, and keeps a history row for the ones that answer.
 *
 * A `switch` with no `default`, so the compiler checks it is total. That is the
 * point of putting it here rather than in the Worker: adding a command forces a
 * decision about whether it belongs on a recipe's history, in the same way
 * `registry.ts` forces a decision about its reducer. Getting it wrong is a build
 * failure, not a timeline with a silent hole in it.
 *
 * "Writes" means *writes the recipe document*. `batch.create` freezes a copy of
 * a recipe and `recipeSnapshot.create` names one, but neither changes the recipe
 * itself, so neither is a point in its history.
 */

import type { Command } from './types';

/**
 * The id of the recipe `command` writes, or `null` if it writes none.
 *
 * `recipe.delete` answers with the id it removes: a deletion is the last event
 * in that recipe's history, and the caller needs to know whose history to close.
 */
export function recipeScopeOf(command: Command): string | null {
  switch (command.type) {
    // The one command that carries the recipe rather than its id.
    case 'recipe.restore':
      return command.payload.recipe.id;

    // Everything that addresses a recipe by id: lifecycle, the scalar setters,
    // the four ingredient lists, both schedules, and the recipe's water section.
    case 'recipe.create':
    case 'recipe.delete':
    case 'recipe.rename':
    case 'recipe.setStyle':
    case 'recipe.setTargetOg':
    case 'recipe.setTargetSrm':
    case 'recipe.setArchived':
    case 'recipe.setBatchSize':
    case 'recipe.setBoilTime':
    case 'recipe.setEquipment':
    case 'recipe.setEfficiency':
    case 'recipe.setCarbonation':
    case 'recipe.setPackaging':
    case 'recipe.setNotes':
    case 'recipe.setTags':
    case 'recipe.scale':
    case 'recipe.revertTo':
    case 'recipe.addFermentable':
    case 'recipe.updateFermentable':
    case 'recipe.removeFermentable':
    case 'recipe.reorderFermentables':
    case 'recipe.addHop':
    case 'recipe.updateHop':
    case 'recipe.removeHop':
    case 'recipe.reorderHops':
    case 'recipe.addYeast':
    case 'recipe.updateYeast':
    case 'recipe.removeYeast':
    case 'recipe.reorderYeasts':
    case 'recipe.addMisc':
    case 'recipe.updateMisc':
    case 'recipe.removeMisc':
    case 'recipe.reorderMiscs':
    case 'recipe.addMashStep':
    case 'recipe.updateMashStep':
    case 'recipe.removeMashStep':
    case 'recipe.reorderMashSteps':
    case 'recipe.addFermentationStep':
    case 'recipe.updateFermentationStep':
    case 'recipe.removeFermentationStep':
    case 'recipe.reorderFermentationSteps':
    case 'water.setSourceProfile':
    case 'water.setDilution':
    case 'water.setSaltAdditions':
    case 'water.setAcidAdditions':
    case 'water.setTargetProfile':
    case 'water.setTargetRule':
    case 'water.setTargetPh':
    case 'water.setKettleTargetPh':
    case 'water.clear':
      return command.payload.recipeId;

    // Documents of their own. A snapshot names a recipe version and a batch
    // freezes one, but neither edits the recipe; equipment, water profiles,
    // blends, journals, inventory and settings live beside it entirely.
    case 'recipeSnapshot.create':
    case 'recipeSnapshot.restore':
    case 'recipeSnapshot.delete':
    case 'recipeSnapshot.rename':
    case 'equipment.create':
    case 'equipment.update':
    case 'equipment.delete':
    case 'equipment.restore':
    case 'mashProfile.create':
    case 'mashProfile.update':
    case 'mashProfile.delete':
    case 'mashProfile.restore':
    case 'fermentationProfile.create':
    case 'fermentationProfile.update':
    case 'fermentationProfile.delete':
    case 'fermentationProfile.restore':
    case 'miscProfile.create':
    case 'miscProfile.update':
    case 'miscProfile.delete':
    case 'miscProfile.restore':
    case 'waterProfile.create':
    case 'waterProfile.update':
    case 'waterProfile.delete':
    case 'waterProfile.restore':
    case 'batch.create':
    case 'batch.restore':
    case 'batch.resnapshot':
    case 'batch.delete':
    case 'batch.rename':
    case 'batch.setStatus':
    case 'batch.setBrewedAt':
    case 'batch.setPackagedOn':
    case 'batch.updateMeasurements':
    case 'batch.addFermentationReading':
    case 'batch.removeFermentationReading':
    case 'batch.setStageNotes':
    case 'batch.toggleStep':
    case 'batch.setStepTimer':
    case 'batch.setInventoryDeducted':
    case 'batch.setPitchedYeast':
    case 'batch.setPackaging':
    case 'batch.setYeastStarter':
    case 'batch.setYeastPitchedAt':
    case 'batch.addCellarTask':
    case 'batch.updateCellarTask':
    case 'batch.removeCellarTask':
    case 'batch.addTasting':
    case 'batch.updateTasting':
    case 'batch.removeTasting':
    case 'batch.addPhoto':
    case 'batch.setPhotoCaption':
    case 'batch.removePhoto':
    case 'blend.create':
    case 'blend.update':
    case 'blend.setStage':
    case 'blend.delete':
    case 'blend.restore':
    case 'blend.addCellarTask':
    case 'blend.updateCellarTask':
    case 'blend.removeCellarTask':
    case 'journal.create':
    case 'journal.update':
    case 'journal.delete':
    case 'journal.restore':
    case 'journal.addBlock':
    case 'journal.setBlock':
    case 'journal.moveBlock':
    case 'journal.removeBlock':
    case 'inventory.add':
    case 'inventory.update':
    case 'inventory.remove':
    case 'inventory.restore':
    case 'inventory.adjustQuantity':
    case 'customIngredient.add':
    case 'customIngredient.update':
    case 'customIngredient.remove':
    case 'customIngredient.restore':
    case 'settings.update':
      return null;

    // Sharing moves a recipe between the workspace and a room, and picks my
    // equipment for it, but writes no recipe document: `recipe.share` removes
    // one, `sharedRecipe.leave` makes a new one, and neither is an edit on a
    // timeline (PLAN-COLLAB A2).
    case 'recipe.share':
    case 'sharedRecipe.join':
    case 'sharedRecipe.leave':
    case 'sharedRecipe.setEquipmentOverride':
      return null;
  }
}
